import type { Level, Opening, Vec2 } from '../hp/types';

export const WALK_BODY_RADIUS = .25;
export const WALK_MAX_STEP = .4;
export interface WalkSupport {
  height: number;
  sample(point: Vec2, height: number): number;
  /**
   * Where the walker lands at `point`, coming from `from` with its feet at `height`, when the footing there is more than a
   * step away; undefined where it may not go. A stairwell joins the floor and the flight below it so (`Viewer.stairSupport`).
   */
  leap?(from: Vec2, point: Vec2, height: number): number | undefined;
  /**
   * The room over the walker's head at `point` with its feet at `height`, less than 0 where the ceiling is too low to
   * stand: under a mansard's slope (`Viewer`). A step that leaves less room than it had there is not taken; one out from
   * under the slope always is.
   */
  headroom?(point: Vec2, height: number): number;
}
export type OpeningTags = ReadonlyMap<string, readonly string[]>;

export function isDoorPortal(opening: Opening, tags?: OpeningTags): boolean {
  if (opening.sill > .01 || opening.height < 1.6) return false;
  const labels = tags?.get(opening.modelRef.id);
  if (labels?.includes('Window') || opening.modelRef.id.startsWith('wall.windows/')) return false;
  if (labels?.some(tag => tag === 'Door' || tag === 'Arch') || opening.modelRef.id.startsWith('wall.doors/')) return true;
  return opening.sill < .01;
}

function penetrations(level: Level, point: Vec2, tags?: OpeningTags, height = 0): number[] {
  return level.walls.map(wall => {
    const dx = wall.end.x - wall.start.x, dz = wall.end.z - wall.start.z, length = Math.hypot(dx, dz);
    if (length < 1e-6) return 0;
    const along = ((point.x - wall.start.x) * dx + (point.z - wall.start.z) * dz) / length;
    const t = Math.max(0, Math.min(length, along));
    const distance = Math.hypot(point.x - wall.start.x - dx * t / length, point.z - wall.start.z - dz * t / length);
    const penetration = wall.thickness / 2 + WALK_BODY_RADIUS - distance;
    if (penetration <= 0) return 0;
    const portal = level.openings.some(opening => opening.wallId === wall.id && isDoorPortal(opening, tags)
      && opening.height + opening.sill >= height + 1.6
      && opening.width >= 2 * WALK_BODY_RADIUS + .1
      && Math.abs(along - opening.offset) <= opening.width / 2 - WALK_BODY_RADIUS - .05);
    return portal ? 0 : penetration;
  });
}

/** A walker standing at `point` would be in a wall of `level`, past a door's gap. */
export function inWall(level: Level, point: Vec2): boolean {
  return penetrations(level, point).some(depth => depth > 0);
}

/** Small swept steps keep thin walls solid even when a frame stalls or running is held. */
export function moveWalker(level: Level, from: Vec2, delta: Vec2, tags: OpeningTags | undefined, support: WalkSupport): Vec2 & { height: number };
export function moveWalker(level: Level, from: Vec2, delta: Vec2, tags?: OpeningTags): Vec2;
export function moveWalker(level: Level, from: Vec2, delta: Vec2, tags?: OpeningTags, support?: WalkSupport): Vec2 & { height?: number } {
  const distance = Math.hypot(delta.x, delta.z);
  let height = support?.height ?? 0;
  if (!Number.isFinite(distance) || distance === 0) return support ? { ...from, height } : { ...from };
  const steps = Math.ceil(distance / .04), step = { x: delta.x / steps, z: delta.z / steps };
  let position = { ...from };
  for (let i = 0; i < steps; i++) {
    const accept = (point: Vec2): boolean => {
      let nextHeight = support?.sample(point, height) ?? height;
      if (!Number.isFinite(nextHeight)) return false;
      if (Math.abs(nextHeight - height) > WALK_MAX_STEP + 1e-6) {
        const landing = support?.leap?.(position, point, height);
        if (landing === undefined || !Number.isFinite(landing)) return false;
        nextHeight = landing;
      }
      const before = penetrations(level, position, tags, height);
      if (!penetrations(level, point, tags, nextHeight).every((depth, index) => depth <= 1e-8
        || (before[index] > 0 && depth <= before[index] + 1e-8))) return false;
      if (support?.headroom) {
        const room = support.headroom(point, nextHeight);
        if (room < 0 && room < support.headroom(position, height) - 1e-6) return false;
      }
      position = point; height = nextHeight; return true;
    };
    const next = { x: position.x + step.x, z: position.z + step.z };
    if (!accept(next)) {
      accept({ x: next.x, z: position.z });
      accept({ x: position.x, z: next.z });
    }
  }
  return support ? { ...position, height } : position;
}
